<div class="p-5 max-w-[1600px] mx-auto">

  <!-- Header -->
  <div class="flex justify-between items-center mb-6">
    <div class="flex items-center gap-3">
      <button mat-icon-button (click)="cancel()" matTooltip="{{ 'generic.cancel' | translate }}">
        <mat-icon>arrow_back</mat-icon>
      </button>
      @if (customerId()) {
        <h1 class="text-2xl font-medium">{{ pageTitle() || ('customers.form.edit-title' | translate) }}</h1>
      } @else {
        <h1 class="text-2xl font-medium">{{ 'customers.form.create-title' | translate }}</h1>
      }
    </div>
    <div class="flex gap-2 items-center">
      @if (saving()) {
        <mat-spinner diameter="24" />
      }
      <button mat-stroked-button (click)="cancel()" [disabled]="saving()">
        {{ 'generic.cancel' | translate }}
      </button>
      <button mat-flat-button color="primary" (click)="save()" [disabled]="saving() || loading()">
        {{ 'customers.form.save' | translate }}
      </button>
    </div>
  </div>

  @if (loading()) {
    <div class="flex justify-center py-20">
      <mat-spinner diameter="48" />
    </div>
  } @else {

    <form class="flex gap-5 items-start" [formGroup]="form" (ngSubmit)="save()">

      <!-- ── Left column (2/3) ── -->
      <div class="flex-[2] flex flex-col gap-5">

        <!-- Personal Info -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'customers.form.section.personal' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>

            <div class="flex gap-4">
              <mat-form-field class="flex-1">
                <mat-label>{{ 'customers.form.firstName' | translate }}</mat-label>
                <input matInput formControlName="firstName" />
                @if (form.controls.firstName.invalid && form.controls.firstName.touched) {
                  <mat-error>{{ 'customers.form.firstName-required' | translate }}</mat-error>
                }
              </mat-form-field>

              <mat-form-field class="flex-1">
                <mat-label>{{ 'customers.form.lastName' | translate }}</mat-label>
                <input matInput formControlName="lastName" />
                @if (form.controls.lastName.invalid && form.controls.lastName.touched) {
                  <mat-error>{{ 'customers.form.lastName-required' | translate }}</mat-error>
                }
              </mat-form-field>
            </div>

            <div class="flex gap-4">
              <mat-form-field class="flex-1">
                <mat-label>{{ 'customers.form.gender' | translate }}</mat-label>
                <mat-select formControlName="genderId">
                  <mat-option [value]="null">—</mat-option>
                  @for (g of codeListsService.genders(); track g.id) {
                    <mat-option [value]="g.id">{{ g.name }}</mat-option>
                  }
                </mat-select>
                @if (form.controls.genderId.invalid && form.controls.genderId.touched) {
                  <mat-error>{{ 'customers.form.gender-required' | translate }}</mat-error>
                }
              </mat-form-field>

              <mat-form-field class="flex-1">
                <mat-label>{{ 'customers.form.status' | translate }}</mat-label>
                <mat-select formControlName="statusId">
                  @for (s of codeListsService.customerStatuses(); track s.id) {
                    <mat-option [value]="s.id">{{ s.name }}</mat-option>
                  }
                </mat-select>
                @if (form.controls.statusId.invalid && form.controls.statusId.touched) {
                  <mat-error>{{ 'customers.form.status-required' | translate }}</mat-error>
                }
              </mat-form-field>

              <mat-form-field class="flex-1">
                <mat-label>{{ 'customers.form.site' | translate }}</mat-label>
                <mat-select formControlName="siteId">
                  <mat-option [value]="null">—</mat-option>
                  @for (s of codeListsService.sites(); track s.id) {
                    <mat-option [value]="s.id">{{ s.name }}</mat-option>
                  }
                </mat-select>
                @if (form.controls.siteId.invalid && form.controls.siteId.touched) {
                  <mat-error>{{ 'customers.form.site-required' | translate }}</mat-error>
                }
              </mat-form-field>
            </div>

          </mat-card-content>
        </mat-card>

        <!-- Contact -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'customers.form.section.contact' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>

            <!-- Email addresses -->
            <p class="text-sm font-medium text-gray-600 mb-2">{{ 'customers.form.emailAddresses' | translate }}</p>

            @for (emailGroup of emailAddresses.controls; track $index; let i = $index) {
              <div class="flex gap-2 items-center mb-2" [formGroup]="emailGroup">
                <mat-form-field class="flex-[2]" subscriptSizing="dynamic">
                  <mat-label>{{ 'customers.form.email' | translate }}</mat-label>
                  <input matInput formControlName="email" type="email" />
                  @if (emailGroup.controls.email.hasError('required') && emailGroup.controls.email.touched) {
                    <mat-error>{{ 'customers.form.email-required' | translate }}</mat-error>
                  } @else if (emailGroup.controls.email.hasError('email') && emailGroup.controls.email.touched) {
                    <mat-error>{{ 'customers.form.email-invalid' | translate }}</mat-error>
                  }
                </mat-form-field>

                <mat-form-field class="flex-1" subscriptSizing="dynamic">
                  <mat-label>{{ 'customers.form.email-label' | translate }}</mat-label>
                  <input matInput formControlName="label" />
                </mat-form-field>

                <button
                  mat-icon-button
                  type="button"
                  [class.text-blue-600]="emailGroup.controls.isPrimary.value"
                  [class.text-gray-300]="!emailGroup.controls.isPrimary.value"
                  (click)="setPrimaryEmail(i)"
                  matTooltip="{{ 'customers.form.set-primary' | translate }}">
                  <mat-icon>{{ emailGroup.controls.isPrimary.value ? 'star' : 'star_border' }}</mat-icon>
                </button>

                <button
                  mat-icon-button
                  type="button"
                  color="warn"
                  (click)="removeEmailAddress(i)"
                  matTooltip="{{ 'customers.form.remove' | translate }}">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            }

            <button mat-stroked-button type="button" class="mt-1 mb-5" (click)="addEmailAddress()">
              <mat-icon>add</mat-icon>
              {{ 'customers.form.add-email' | translate }}
            </button>

            <!-- Phone numbers -->
            <p class="text-sm font-medium text-gray-600 mb-2">{{ 'customers.form.phoneNumbers' | translate }}</p>

            @for (phoneGroup of phoneNumbers.controls; track $index; let i = $index) {
              <div class="flex gap-2 items-center mb-2" [formGroup]="phoneGroup">
                <mat-form-field class="flex-[2]" subscriptSizing="dynamic">
                  <mat-label>{{ 'customers.form.phoneNumber' | translate }}</mat-label>
                  <input matInput formControlName="number" />
                </mat-form-field>

                <mat-form-field class="flex-1" subscriptSizing="dynamic">
                  <mat-label>{{ 'customers.form.phone-type' | translate }}</mat-label>
                  <mat-select formControlName="phoneNumberTypeId">
                    <mat-option [value]="null">—</mat-option>
                    @for (t of codeListsService.phoneNumberTypes(); track t.id) {
                      <mat-option [value]="t.id">{{ t.name }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <button
                  mat-icon-button
                  type="button"
                  [class.text-blue-600]="phoneGroup.controls.isPrimary.value"
                  [class.text-gray-300]="!phoneGroup.controls.isPrimary.value"
                  (click)="setPrimaryPhone(i)"
                  matTooltip="{{ 'customers.form.set-primary' | translate }}">
                  <mat-icon>{{ phoneGroup.controls.isPrimary.value ? 'star' : 'star_border' }}</mat-icon>
                </button>

                <button
                  mat-icon-button
                  type="button"
                  color="warn"
                  (click)="removePhoneNumber(i)"
                  matTooltip="{{ 'customers.form.remove' | translate }}">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>
            }

            <button mat-stroked-button type="button" class="mt-1" (click)="addPhoneNumber()">
              <mat-icon>add</mat-icon>
              {{ 'customers.form.add-phone' | translate }}
            </button>

          </mat-card-content>
        </mat-card>

        <!-- Addresses -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'customers.form.section.address' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>

            <div class="flex gap-2 mb-2">
              <mat-form-field class="flex-[2]" subscriptSizing="dynamic">
                <mat-label>{{ 'customers.form.address' | translate }}</mat-label>
                <input matInput formControlName="address" />
              </mat-form-field>

              <mat-form-field class="w-32" subscriptSizing="dynamic">
                <mat-label>{{ 'customers.form.postalCode' | translate }}</mat-label>
                <input matInput formControlName="postalCode" />
              </mat-form-field>
            </div>

            <div class="flex gap-2">
              <mat-form-field class="flex-1" subscriptSizing="dynamic">
                <mat-label>{{ 'customers.form.city' | translate }}</mat-label>
                <input matInput formControlName="city" />
              </mat-form-field>

              <mat-form-field class="flex-1" subscriptSizing="dynamic">
                <mat-label>{{ 'customers.form.country' | translate }}</mat-label>
                <mat-select formControlName="countryId">
                  <mat-option [value]="null">—</mat-option>
                  @for (c of codeListsService.countries(); track c.id) {
                    <mat-option [value]="c.id">{{ c.name }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>

          </mat-card-content>
        </mat-card>

      </div>

      <!-- ── Right column (1/3) ── -->
      <div class="flex-1 flex flex-col gap-5">

        <!-- Settings -->
        <mat-card>
          <mat-card-header>
            <mat-card-title>{{ 'customers.form.section.settings' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="flex flex-col gap-4">

              <div class="flex items-center justify-between">
                <span class="text-sm">{{ 'customers.form.isActive' | translate }}</span>
                <mat-slide-toggle formControlName="isActive" />
              </div>

              <div class="flex items-center justify-between">
                <span class="text-sm">{{ 'customers.form.newsletter' | translate }}</span>
                <mat-slide-toggle formControlName="newsletter" />
              </div>

              <div class="flex items-center justify-between">
                <span class="text-sm">{{ 'customers.form.isDummyCustomer' | translate }}</span>
                <mat-slide-toggle formControlName="isDummyCustomer" />
              </div>

            </div>
          </mat-card-content>
        </mat-card>

        <!-- Recent Reservations (edit mode only) -->
        @if (customerId()) {
          <mat-card>
            <mat-card-header>
              <mat-card-title>{{ 'customers.form.section.recent-reservations' | translate }}</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              @if (recentReservations().length === 0) {
                <p class="text-sm">{{ 'customers.form.no-reservations' | translate }}</p>
              } @else {
                <div class="flex flex-col gap-2 pt-2">
                  @for (r of recentReservations(); track r.reservationNumber) {
                    <div class="p-2 rounded-lg flex flex-col gap-1 bg-surface-container-high">
                      <div class="flex items-center justify-between gap-2">
                        <span class="text-sm font-medium">{{ r.reservationNumber }}</span>
                        @if (r.statusDescription) {
                          <span [class]="getReservationStatusClass(r.statusLabel)">{{ r.statusDescription }}</span>
                        }
                      </div>
                      <span class="text-xs truncate">{{ r.waterTitle }} - {{ r.arrivalDate | date: 'dd MMM yyyy' }}</span>
                    </div>
                  }
                </div>
              }
            </mat-card-content>
          </mat-card>
        }

      </div>

    </form>

  }

</div>
